import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import type { SupportedCurrency } from '@sorare/core/src/__generated__/globalTypes';
import { HeadlineS } from '@sorare/core/src/atoms/typography';
import { useSnackNotificationContext } from '@sorare/core/src/contexts/snackNotification';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useCurrentUserCurrencies } from '@sorare/core/src/hooks/useCurrentUserCurrencies';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { useSendUserEngagementIncrement } from '@sorare/core/src/hooks/useSendUserEngagementIncrement';
import { sendSafeError } from '@sorare/core/src/lib/error';
import { payment } from '@sorare/core/src/lib/glossary';
import {
  getCurrenciesFromSettlementCurrencies,
  getCurrenciesFromSettlementCurrenciesWhenCannotSwapCrypto,
} from '@sorare/core/src/lib/settlementCurrencies';
import { UserEngagementScoreIncrement_Source } from '@sorare/core/src/protos/webview/channel/native_messages';

import { LazyPaymentProvider } from '#src/components/buyActions/LazyPaymentProvider/index.tsx';
import { CardOverview } from '#src/components/buyActions/PaymentBox/CardOverview/index.tsx';
import { useBuyConfirmationContext } from '#src/contexts/buyingConfirmation/index.tsx';
import { useAcceptOffer } from '#src/hooks/offers/useAcceptOffer.ts';

import type {
  BuyPaymentFlowQuery,
  BuyPaymentFlowQueryVariables,
} from './__generated__/index.graphql.ts';

type Props = {
  onClose: () => void;
  offerId: string;
};

const BUY_PAYMENT_FLOW_QUERY = gql`
  query BuyPaymentFlowQuery($id: String!) {
    tokens {
      offer(id: $id) {
        id
        settlementCurrencies
        endDate
        sender {
          ... on User {
            slug
            nickname
          }
          ... on AnonymousUser {
            id
          }
        }
        senderSide {
          id
          anyCards {
            slug
            sport
            ...CardOverview_anyCard
          }
        }
        receiverSide {
          id
          amounts {
            ...useMonetaryAmount_monetaryAmount
          }
        }
      }
    }
  }
  ${useMonetaryAmount.fragments.monetaryAmount}
  ${CardOverview.fragments.anyCard}
` as TypedDocumentNode<BuyPaymentFlowQuery, BuyPaymentFlowQueryVariables>;

export const BuyPaymentFlow = ({ offerId, onClose }: Props) => {
  const {
    flags: { useCannotSwapCrytpo = true },
  } = useFeatureFlags();
  const { cryptocurrency } = useCurrentUserCurrencies();

  const { showNotification } = useSnackNotificationContext();
  const { setShowBuyingConfirmation } = useBuyConfirmationContext();
  const acceptOffer = useAcceptOffer();
  const { sendUserEngagementIncrement } = useSendUserEngagementIncrement();

  const { data, error } = useQuery(BUY_PAYMENT_FLOW_QUERY, {
    variables: {
      id: idFromObject(offerId),
    },
  });

  const liveSingleSaleOffer = data?.tokens?.offer;

  const onPaymentSuccess = useCallback(() => {
    setShowBuyingConfirmation(true);
    onClose();
  }, [setShowBuyingConfirmation, onClose]);

  const { toMonetaryAmount } = useMonetaryAmount();

  if (error) {
    // This should not happen. Temporary added for monitoring purposes.
    sendSafeError(`Offer not found for id: ${offerId}`);
    showNotification('errors', { errors: [error.message] });
    onClose();
    return null;
  }

  // Don't show the offer to users if it is not live yet
  if (!liveSingleSaleOffer) return null;

  const { id, receiverSide, settlementCurrencies, sender, senderSide } =
    liveSingleSaleOffer;

  const amounts = toMonetaryAmount(receiverSide.amounts);

  const { anyCards } = senderSide;

  const buyWithEth = async ({
    supportedCurrency,
  }: {
    supportedCurrency: SupportedCurrency;
  }) => {
    const { errors } = await acceptOffer({
      offerId: id,
      supportedCurrency,
      receiveCards: [],
      attemptReference: null,
      conversionCreditIds: [],
      useAvailableCredits: false,
    });

    if (Array.isArray(errors) && errors?.length > 0) {
      return { err: errors };
    }

    sendUserEngagementIncrement(
      45,
      UserEngagementScoreIncrement_Source.PURCHASE_CARD
    );

    return onPaymentSuccess();
  };

  const currencies = (() => {
    if (useCannotSwapCrytpo) {
      return getCurrenciesFromSettlementCurrenciesWhenCannotSwapCrypto(
        settlementCurrencies,
        cryptocurrency
      );
    }
    return getCurrenciesFromSettlementCurrencies(settlementCurrencies);
  })();

  return (
    <LazyPaymentProvider
      paymentProps={{
        objectId: id,
        onSuccess: onPaymentSuccess,
        onSubmit: buyWithEth,
        price: amounts,
        cta: payment.confirmAndPay,
        applyCreditCardFees: true,
        currencies,
        sport: anyCards[0].sport,
        seller: ('nickname' in sender && sender?.nickname) || undefined,
        usePaypal: false,
        canUseTopUpWalletWithCC: false,
        confirmationProviderStateProps: {
          tokenOfferId: liveSingleSaleOffer?.id,
        },
      }}
      paymentDialogProps={{
        onClose,
        title: (
          <HeadlineS as="h5">
            <FormattedMessage {...payment.paymentBoxTitle} />
          </HeadlineS>
        ),
        tokenPreview: <CardOverview cards={anyCards} />,
      }}
    />
  );
};
